import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './pages.css';

const AuthPage = () => {
  const navigate = useNavigate();

  const [isRegistering, setIsRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const toggleForm = () => {
    setIsRegistering(!isRegistering);
    setEmail('');
    setLoginPassword('');
    setRegisterPassword('');
    setConfirmPassword('');
    setAuthMessage('');
    setPasswordError('');
  };

  // Handle user registration
  const handleRegister = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setAuthMessage('');

    if (registerPassword !== confirmPassword) {
      setPasswordError('❌ Passwords do not match');
      return;
    }

    localStorage.setItem('pendingFullName', fullName);
    localStorage.setItem('pendingPhone', phone);
    localStorage.setItem('pendingEmail', email);

    const { error } = await supabase.auth.signUp({
  email,
  password: registerPassword,
});


    if (error) {
      setAuthMessage(`❌ ${error.message}`);
      return;
    }

    setAuthMessage(
      '✅ Registration successful! Please check your email to verify your account before logging in.'
    );

    setTimeout(() => window.location.reload(), 5000);
  };

  // Handle user login
const handleLogin = async (e) => {
  e.preventDefault();
  setAuthMessage('');

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password: loginPassword,
  });

  if (error) {
    setAuthMessage(`❌ ${error.message}`);
    return;
  }

  const user = data.user;
  if (!user) return;

  // Check if user profile exists
  const { data: existingProfile } = await supabase
    .from('users')
    .select('*')
    .eq('id', user.id)
    .single();

  let role = 'user';

  if (!existingProfile) {
    const fullNamePending = localStorage.getItem('pendingFullName');
    const phonePending = localStorage.getItem('pendingPhone');

    const { error: insertError } = await supabase.from('users').insert([{
      id: user.id,
      full_name: fullNamePending,
      email: user.email,
      phone: phonePending,
      role: 'user',
    }]);

    if (insertError) {
      setAuthMessage(`⚠️ Logged in, but failed to save profile: ${insertError.message}`);
      return;
    }
  } else {
    role = existingProfile.role || 'user';
  }

  // ✅ Updated redirect logic
  if (role === 'admin') {
    // Send Supabase access token via URL to admin subdomain
    const token = data.session.access_token;
    window.location.href = `https://admin.eventfind.me/admin?token=${token}`;
  } else {
    navigate('/');
  }
};


  // Check URL for email confirmation or existing session
  useEffect(() => {
    const url = new URL(window.location.href);
    const type = url.searchParams.get('type');
    const accessToken = url.searchParams.get('access_token');

    if (type === 'signup' && accessToken) {
      window.history.replaceState({}, document.title, '/login');
      setAuthMessage('✅ Email confirmed! You can now log in.');
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate('/');
    });
  }, [navigate]);

  return (
    <div className={`auth-container ${isRegistering ? 'register-mode' : ''}`}>
      <div className="auth-box">
        <div className="form-container">
          {/* Login Form */}
          {!isRegistering && (
            <form className="form login-form" onSubmit={handleLogin}>
              <h2>Welcome Back</h2>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <div className="password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="show-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <button type="submit" className="submit-btn">Login</button>
              {authMessage && <p className="error-msg">{authMessage}</p>}
              <p>
                Don’t have an account?{' '}
                <span className="toggle-link" onClick={toggleForm}>Register</span>
              </p>
            </form>
          )}

          {/* Register Form */}
          {isRegistering && (
            <form className="form register-form" onSubmit={handleRegister}>
              <h2>Create Account</h2>
              <input
                type="text"
                placeholder="Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <div className="password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={registerPassword}
                  onChange={(e) => setRegisterPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="show-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <input
                type="tel"
                placeholder="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              {passwordError && <p className="error-msg">{passwordError}</p>}
              {authMessage && <p className="error-msg">{authMessage}</p>}
              <label className="checkbox">
                <input type="checkbox" required /> I accept Terms and Privacy Policy
              </label>
              <button type="submit" className="submit-btn">Register</button>
              <p>
                Already have an account?{' '}
                <span className="toggle-link" onClick={toggleForm}>Login</span>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
